import { parseStatementDate as parseDate, parseStatementDateTime as parseDateTime } from "./statement-date.ts";
import { sourceFtNumber } from "./ft-number.ts";
export type Language = "id" | "en";
export type Direction = "CREDIT" | "DEBIT" | "";
export type TransactionStatus = "SUCCESS" | "FAILED" | "PENDING" | "";

export type DataQualityField =
  | "amount"
  | "direction"
  | "transactionDate"
  | "transactionTime"
  | "paymentRef"
  | "sourceName"
  | "sourceBank"
  | "beneficiaryName"
  | "beneficiaryBank"
  | "subject"
  | "balance";

export type DataReviewStatus = "complete" | "needs_review" | "reviewed";
export type CategoryAssignmentSource = "manual" | "rule" | "learned_auto" | "unassigned" | "legacy";

export type Transaction = {
  id: string;
  paymentRef: string;
  ftNumber?: string;
  sourceAccount: string;
  beneficiaryAccount: string;
  amount: number;
  date: string;
  transactionTimestamp?: string;
  createdDate?: string;
  updatedDate?: string;
  subject: string;
  /** Owner-authored description. The imported bank narrative remains in subject. */
  internalDescription?: string;
  sourceName?: string;
  beneficiaryName?: string;
  sourceBank?: string;
  beneficiaryBank?: string;
  balance?: number;
  uniqueCode: string;
  businessUnit: string;
  businessUnits?: string[];
  brandCandidates?: string[];
  deletedAt?: string;
  updatedAt?: string;
  /** How the business unit was assigned. Added in v0.7; older rows remain valid. */
  businessUnitMappingSource?: "manual" | "recipient" | "code" | "legacy" | "description";
  transferType: string;
  status: TransactionStatus;
  direction: Direction;
  category: string;
  subcategory?: string;
  categoryRuleId?: string;
  categoryRuleConflict?: boolean;
  categoryAssignmentSource?: CategoryAssignmentSource;
  categoryHistory?: { uniqueCode: string; direction: Direction; brandContext: string; category: string }[];
  /** Optional v0.6 reporting account. Existing v0.5 rows remain valid without it. */
  financialAccount?: string;
  financialMappingSource?: "manual" | "rule" | "category";
  accountLabel: string;
  sourceFile: string;
  /** Original file row index when known (not a primary identity). */
  sourceRowNumber?: number;
  /** Origin of the canonical row. Older rows are treated as imported data. */
  entryMethod?: "import" | "manual";
  sourceBatchIds: string[];
  sourceFiles: string[];
  originalPayload?: Record<string, unknown>;
  fingerprint: string;
  identityKeys: string[];
  missingFields: DataQualityField[];
  confirmedEmptyFields: DataQualityField[];
  dataReviewStatus: DataReviewStatus;
};

/**
 * v0.12 starts without business-category assumptions. These exports remain for
 * backwards-compatible imports; live category options are loaded from the
 * database and the only technical fallback is `uncategorized`.
 */
export const incomeCategories: string[] = [];
export const costCategories: string[] = [];
export const allCategories: string[] = [];

export const categoryLabels: Record<string, { id: string; en: string }> = {
  uncategorized: { id: "Belum Dikategorikan", en: "Uncategorized" },
};

const headerAliases: Record<string, string[]> = {
  paymentRef: [
    "payment ref transaction id subject",
    "payment ref",
    "payment reference",
    "transaction id",
    "id transaksi",
    "referensi pembayaran",
    "id pembayaran",
    "no referensi",
    "nomor referensi",
  ],
  subject: ["subject", "description", "keterangan", "deskripsi", "remark", "remarks", "uraian"],
  sourceAccount: ["source account", "rekening sumber", "nomor rekening sumber", "debit account"],
  sourceName: ["sender name", "source name", "nama pengirim"],
  sourceBank: ["sender bank", "source bank", "bank pengirim"],
  beneficiaryAccount: [
    "beneficiary account",
    "rekening tujuan",
    "nomor rekening tujuan",
    "credit account",
  ],
  beneficiaryName: ["beneficiary name", "recipient name", "nama penerima"],
  beneficiaryBank: ["beneficiary bank", "recipient bank", "bank penerima"],
  amount: ["amount", "nominal", "jumlah", "transaction amount", "nilai transaksi"],
  debit: ["debit", "debet", "withdrawal", "pengeluaran"],
  credit: ["credit", "kredit", "deposit", "pemasukan"],
  date: [
    "payment date",
    "transaction date",
    "tanggal pembayaran",
    "tanggal transaksi",
    "tanggal mutasi",
    "date",
    "waktu transaksi",
  ],
  createdDate: ["created date", "tanggal dibuat", "created at"],
  updatedDate: ["updated date", "tanggal diperbarui", "updated at"],
  transferType: ["payment trf type", "transfer type", "jenis transfer", "transaction type", "jenis transaksi"],
  direction: ["type", "transaction direction", "debit credit", "db cr", "tipe"],
  status: ["transaction status", "status transaksi", "payment status", "status"],
  accountLabel: ["account name", "nama rekening sumber", "statement account", "accountlabel"],
  balance: ["balance", "saldo", "saldo riil"],
};

/** Legacy fallback list; v0.7 loads the active brand master from Supabase. */
export const businessUnits = ["eventkita", "meaningful_ways", "tumbuh_bersama", "unassigned"] as const;

export const businessUnitLabels: Record<string, { id: string; en: string }> = {
  meaningful_ways: { id: "Meaningful Ways (MW)", en: "Meaningful Ways (MW)" },
  tumbuh_bersama: { id: "Titik Balik (TB)", en: "Titik Balik (TB)" },
  eventkita: { id: "EventKita", en: "EventKita" },
  unassigned: { id: "Belum ditentukan", en: "Unassigned" },
};

function simplify(value: unknown) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function findValue(row: Record<string, unknown>, field: keyof typeof headerAliases) {
  const keys = Object.keys(row);
  for (const alias of headerAliases[field]) {
    const exact = keys.find((key) => simplify(key) === alias);
    if (exact && row[exact] !== undefined && row[exact] !== "") return row[exact];
  }
  for (const alias of headerAliases[field]) {
    const partial = keys.find((key) => simplify(key).includes(alias));
    if (partial && row[partial] !== undefined && row[partial] !== "") return row[partial];
  }
  return "";
}

export function parseAmount(value: unknown) {
  if (typeof value === "number") return Math.abs(value);
  let raw = String(value ?? "").trim().replace(/\s/g, "").replace(/IDR|RP/gi, "");
  if (!raw) return 0;
  raw = raw.replace(/-/g, "");
  const comma = raw.lastIndexOf(",");
  const dot = raw.lastIndexOf(".");
  if (comma > dot) {
    const decimal = raw.length - comma - 1 === 2;
    raw = decimal ? raw.replace(/\./g, "").replace(",", ".") : raw.replace(/,/g, "");
  } else if (dot > comma) {
    const decimal = raw.length - dot - 1 === 2;
    raw = decimal ? raw.replace(/,/g, "") : raw.replace(/\./g, "").replace(/,/g, "");
  }
  return Math.abs(Number(raw.replace(/[^0-9.]/g, "")) || 0);
}

// Shared calendar parser: preserve the statement's date and wall-clock time.
export { parseStatementDate as parseDate, parseStatementDateTime as parseDateTime } from "./statement-date.ts";

function normalizeStatus(value: unknown): TransactionStatus {
  const status = simplify(value);
  if (!status) return "";
  if (/fail|gagal|reject|batal|cancel/.test(status)) return "FAILED";
  if (/pending|process|proses|tertunda/.test(status)) return "PENDING";
  if (/success|sukses|berhasil|complete|completed|selesai/.test(status)) return "SUCCESS";
  return "";
}

function detectDirection(row: Record<string, unknown>) {
  const rawCredit = findValue(row, "credit");
  const rawDebit = findValue(row, "debit");
  const credit = parseAmount(rawCredit);
  const debit = parseAmount(rawDebit);
  if (credit > 0) return { direction: "CREDIT" as Direction, amount: credit };
  if (debit > 0) return { direction: "DEBIT" as Direction, amount: debit };
  const exactMarker = (name: "db" | "cr") => Object.entries(row)
    .find(([key]) => simplify(key) === name)?.[1];
  if (/\bcr\b/.test(simplify(exactMarker("cr")))) {
    return { direction: "CREDIT" as Direction, amount: parseAmount(findValue(row, "amount")) };
  }
  if (/\bdb\b/.test(simplify(exactMarker("db")))) {
    return { direction: "DEBIT" as Direction, amount: parseAmount(findValue(row, "amount")) };
  }
  const directionRaw = simplify(findValue(row, "direction") || findValue(row, "transferType"));
  if (/\b(cr|credit|kredit|masuk|income|deposit)\b/.test(directionRaw)) {
    return { direction: "CREDIT" as Direction, amount: parseAmount(findValue(row, "amount")) };
  }
  if (/\b(db|debit|debet|keluar|expense|withdrawal)\b/.test(directionRaw)) {
    return { direction: "DEBIT" as Direction, amount: parseAmount(findValue(row, "amount")) };
  }
  return { direction: "" as Direction, amount: parseAmount(findValue(row, "amount")) };
}

/** Kode bisnis EventKita: tiga digit terakhir dari nominal rupiah. */
export function uniqueCodeFromAmount(amount: number) {
  return amount > 0 ? String(Math.trunc(Math.abs(amount)) % 1_000).padStart(3, "0") : "";
}

export function detectBusinessUnit(value: string) {
  void value;
  return "unassigned";
}

export function autoCategory(subject: string, direction: Direction) {
  void subject;
  void direction;
  return "uncategorized";
}

export function hashString(value: string) {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36).toUpperCase();
}

function normalizeNarrative(value: string) {
  return simplify(value)
    .replace(/^bifast\s+/, "")
    .replace(/\s+trf\s+(?:dari|ke)\s+.*$/, "")
    .trim();
}

function identityKey(kind: string, value: string) {
  return `${kind}:${JSON.stringify(value.toUpperCase())}`;
}

function unique(values: string[]) {
  return [...new Set(values.filter(Boolean))];
}

export function recipientAccountLabel(transaction: Pick<Transaction, "beneficiaryName" | "beneficiaryBank">) {
  return [transaction.beneficiaryName, transaction.beneficiaryBank].filter(Boolean).join(" · ");
}

export const criticalDataQualityFields: DataQualityField[] = ["amount", "direction"];

export function transactionMissingFields(transaction: Pick<Transaction,
  "amount" | "direction" | "date" | "transactionTimestamp" | "paymentRef" |
  "sourceName" | "sourceBank" | "beneficiaryName" | "beneficiaryBank" |
  "subject" | "balance" | "status"
>): DataQualityField[] {
  const missing: DataQualityField[] = [];
  if (!(transaction.amount > 0)) missing.push("amount");
  if (!transaction.direction) missing.push("direction");
  if (!transaction.date) missing.push("transactionDate");
  else if (!transaction.transactionTimestamp) missing.push("transactionTime");
  if (!transaction.paymentRef) missing.push("paymentRef");
  if (!transaction.sourceName) missing.push("sourceName");
  if (!transaction.sourceBank) missing.push("sourceBank");
  if (!transaction.beneficiaryName) missing.push("beneficiaryName");
  if (!transaction.beneficiaryBank) missing.push("beneficiaryBank");
  if (!transaction.subject) missing.push("subject");
  if (transaction.balance === undefined) missing.push("balance");
  return missing;
}

function transactionIdentityParts(transaction: Pick<Transaction,
  "accountLabel" | "paymentRef" | "transactionTimestamp" | "date" | "direction" |
  "amount" | "subject" | "sourceAccount" | "beneficiaryAccount" | "status"
>) {
  const scope = simplify(transaction.accountLabel);
  const rawFingerprint = [
    scope,
    transaction.paymentRef,
    transaction.transactionTimestamp || transaction.date,
    transaction.direction,
    transaction.amount,
    transaction.subject,
    transaction.sourceAccount,
    transaction.beneficiaryAccount,
  ].join("|");
  const identityKeys = unique([
    identityKey("SOURCE", rawFingerprint),
    transaction.paymentRef && transaction.date && transaction.direction && transaction.amount > 0
      ? identityKey("REF", JSON.stringify([scope, transaction.paymentRef.trim(), transaction.date, transaction.direction, transaction.amount]))
      : "",
    (transaction.sourceAccount || transaction.beneficiaryAccount) && transaction.date && transaction.direction && transaction.amount > 0
      ? identityKey("ACCOUNT", [scope, transaction.date, transaction.direction, transaction.amount, simplify(transaction.sourceAccount), simplify(transaction.beneficiaryAccount)].join("|"))
      : "",
    normalizeNarrative(transaction.subject) && transaction.date && transaction.direction && transaction.amount > 0
      ? identityKey("DETAIL", [scope, transaction.date, transaction.direction, transaction.amount, normalizeNarrative(transaction.subject)].join("|"))
      : "",
    transaction.transactionTimestamp && transaction.direction && transaction.amount > 0
      ? identityKey("MOMENT", [scope, transaction.transactionTimestamp, transaction.direction, transaction.amount].join("|"))
      : "",
  ]);
  return { fingerprint: hashString(rawFingerprint), identityKeys };
}

export function refreshTransactionDerived<T extends Transaction>(transaction: T): T {
  const recoveredFt = Object.hasOwn(transaction, "ftNumber") ? "" : sourceFtNumber(transaction.originalPayload || {});
  const missingFields = transactionMissingFields(transaction);
  const confirmedEmptyFields = unique(transaction.confirmedEmptyFields || [])
    .filter((field): field is DataQualityField => missingFields.includes(field as DataQualityField));
  const unresolvedFields = missingFields.filter((field) => !confirmedEmptyFields.includes(field));
  const identity = transactionIdentityParts(transaction);
  const categoryHistory = [...(transaction.categoryHistory || [])];
  if (!transaction.deletedAt && transaction.category !== "uncategorized" && (transaction.categoryAssignmentSource === "manual" || transaction.categoryAssignmentSource === "rule")) {
    const context = [...new Set((transaction.businessUnits ?? [transaction.businessUnit]).filter((value) => value && value !== "unassigned"))].sort().join("+") || "unassigned";
    const decision = { uniqueCode: uniqueCodeFromAmount(transaction.amount), direction: transaction.direction, brandContext: context, category: transaction.category };
    if (!categoryHistory.some((value) => value.uniqueCode === decision.uniqueCode && value.direction === decision.direction && value.brandContext === decision.brandContext && value.category === decision.category)) categoryHistory.push(decision);
  }
  return {
    ...transaction,
    ...(recoveredFt ? { ftNumber: recoveredFt } : {}),
    uniqueCode: uniqueCodeFromAmount(transaction.amount),
    fingerprint: identity.fingerprint,
    identityKeys: identity.identityKeys,
    ...(categoryHistory.length || transaction.categoryHistory ? { categoryHistory } : {}),
    missingFields,
    confirmedEmptyFields,
    dataReviewStatus: missingFields.length
      ? unresolvedFields.length ? "needs_review" : "reviewed"
      : "complete",
  };
}

export function normalizeRows(rows: Record<string, unknown>[], sourceFile: string): Transaction[] {
  return rows
    .map<Transaction | null>((row, index) => {
      const hasAnyValue = Object.values(row).some((value) => String(value ?? "").trim() !== "");
      if (!hasAnyValue) return null;
      const detected = detectDirection(row);
      const ftNumber = sourceFtNumber(row);
      // Keep FT-only statements compatible with the existing Reference column,
      // while an explicitly supplied reference always takes precedence.
      const hasReferenceHeader = Object.keys(row).some(key => headerAliases.paymentRef.some(alias => simplify(key).includes(alias)));
      const rawReference = String(findValue(row, "paymentRef") || (hasReferenceHeader ? "" : ftNumber)).trim();
      const compositeParts = rawReference.split(/\s+[–—-]\s+/).map((value) => value.trim()).filter(Boolean);
      const isCompositeReference = compositeParts.length >= 2;
      const subjectValue = String(findValue(row, "subject") || "").trim();
      const subject = isCompositeReference && compositeParts.length >= 3
        ? compositeParts.slice(2).join(" - ")
        : subjectValue;
      const rawDate = findValue(row, "date");
      const date = parseDate(rawDate);
      const createdDate = String(findValue(row, "createdDate") || "").trim();
      const updatedDate = String(findValue(row, "updatedDate") || "").trim();
      const transactionTimestamp = parseDateTime(rawDate) || parseDateTime(updatedDate) || parseDateTime(createdDate);
      const paymentRef = isCompositeReference ? compositeParts[0] : rawReference;
      const sourceAccount = String(findValue(row, "sourceAccount") || "").trim();
      const beneficiaryAccount = String(findValue(row, "beneficiaryAccount") || "").trim();
      const sourceName = String(findValue(row, "sourceName") || "").trim();
      const explicitRekening = Object.entries(row).find(([key]) => simplify(key) === "rekening")?.[1];
      const beneficiaryName = String(findValue(row, "beneficiaryName") || explicitRekening || "").trim();
      const sourceBank = String(findValue(row, "sourceBank") || "").trim();
      const beneficiaryBank = String(findValue(row, "beneficiaryBank") || "").trim();
      const transferType = String(findValue(row, "transferType") || "").trim();
      const status = normalizeStatus(findValue(row, "status"));
      const explicitStatementAccount = Object.entries(row).find(([key]) => simplify(key) === "account")?.[1];
      const accountLabel = String(findValue(row, "accountLabel") || explicitStatementAccount || "").trim();
      // Brand assignment is handled by an explicit recipient-name mapping.
      // Unknown recipients stay unassigned instead of being guessed from free text.
      const businessUnit = "unassigned";
      const base: Transaction = {
        id: `TX-PENDING-${index + 1}`,
        paymentRef,
        ftNumber,
        sourceAccount,
        beneficiaryAccount,
        amount: detected.amount,
        date,
        transactionTimestamp,
        createdDate,
        updatedDate,
        subject,
        sourceName,
        beneficiaryName,
        sourceBank,
        beneficiaryBank,
        balance: String(findValue(row, "balance") ?? "").trim() === "" ? undefined : parseAmount(findValue(row, "balance")),
        uniqueCode: uniqueCodeFromAmount(detected.amount),
        businessUnit,
        businessUnitMappingSource: undefined,
        transferType,
        status,
        direction: detected.direction,
        category: "uncategorized",
        categoryAssignmentSource: "unassigned",
        accountLabel,
        sourceFile,
        entryMethod: "import",
        sourceBatchIds: [],
        sourceFiles: [sourceFile],
        originalPayload: { ...row },
        fingerprint: "",
        identityKeys: [],
        missingFields: [],
        confirmedEmptyFields: [],
        dataReviewStatus: "complete",
      };
      const normalized = refreshTransactionDerived(base);
      return { ...normalized, id: `TX-${normalized.fingerprint}-${index + 1}` } satisfies Transaction;
    })
    .filter((value): value is Transaction => value !== null);
}

function decodeHtml(value: string) {
  if (typeof DOMParser !== "undefined") {
    return new DOMParser().parseFromString(`<body>${value}</body>`, "text/html").body.textContent?.trim() || "";
  }
  return value
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();
}

export function parseHtmlSpreadsheet(text: string) {
  return [...text.matchAll(/<tr\b[^>]*>([\s\S]*?)<\/tr>/gi)]
    .map((row) => [...row[1].matchAll(/<t[dh]\b[^>]*>([\s\S]*?)<\/t[dh]>/gi)].map((cell) => decodeHtml(cell[1])))
    .filter((row) => row.length > 0);
}

function mergeTransaction(existing: Transaction, incoming: Transaction): Transaction {
  const prefer = (first: string | undefined, second: string | undefined) => first || second || "";
  const richer = (first: string, second: string) => second.length > first.length ? second : first;
  return refreshTransactionDerived({
    ...existing,
    paymentRef: prefer(existing.paymentRef, incoming.paymentRef),
    sourceAccount: prefer(existing.sourceAccount, incoming.sourceAccount),
    beneficiaryAccount: prefer(existing.beneficiaryAccount, incoming.beneficiaryAccount),
    sourceName: prefer(existing.sourceName, incoming.sourceName),
    beneficiaryName: prefer(existing.beneficiaryName, incoming.beneficiaryName),
    sourceBank: prefer(existing.sourceBank, incoming.sourceBank),
    beneficiaryBank: prefer(existing.beneficiaryBank, incoming.beneficiaryBank),
    transactionTimestamp: existing.transactionTimestamp || incoming.transactionTimestamp,
    subject: richer(existing.subject, incoming.subject),
    transferType: prefer(existing.transferType, incoming.transferType),
    balance: existing.balance ?? incoming.balance,
    uniqueCode: uniqueCodeFromAmount(existing.amount),
    businessUnit: existing.businessUnit && existing.businessUnit !== "unassigned"
      ? existing.businessUnit
      : incoming.businessUnit || "unassigned",
    category: existing.category === "uncategorized" ? incoming.category : existing.category,
    financialAccount: existing.financialAccount || incoming.financialAccount,
    financialMappingSource: existing.financialMappingSource || incoming.financialMappingSource,
    sourceFile: unique([...existing.sourceFile.split(" · "), ...incoming.sourceFile.split(" · ")]).join(" · "),
    sourceFiles: unique([
      ...(existing.sourceFiles || [existing.sourceFile]),
      ...(incoming.sourceFiles || [incoming.sourceFile]),
    ]),
    sourceBatchIds: unique([
      ...(existing.sourceBatchIds || []),
      ...(incoming.sourceBatchIds || []),
    ]),
    identityKeys: unique([...(existing.identityKeys || []), ...(incoming.identityKeys || [])]),
    confirmedEmptyFields: unique([
      ...(existing.confirmedEmptyFields || []),
      ...(incoming.confirmedEmptyFields || []),
    ]) as DataQualityField[],
  });
}

export type DuplicateReason = "reference" | "sender" | "dateTime" | "description" | "amount" | "direction" | "account";

export type DuplicateCandidate = {
  transaction: Transaction;
  reasons: DuplicateReason[];
  referenceConflict?: boolean;
  beneficiaryBankMatch?: "same" | "different" | "missing";
  ftNumberMatch?: "same" | "different" | "missing";
};

export type DuplicateReviewItem = {
  incomingIndex: number;
  incoming: Transaction;
  candidates: DuplicateCandidate[];
  automaticTargetId?: string;
  referenceConflict?: boolean;
};

export type DuplicateAnalysis = {
  referenceMatchCount: number;
  newTransactionCount: number;
  reviewItems: DuplicateReviewItem[];
};

export type CodeCategoryRuleLike = {
  uniqueCode: string;
  direction: Direction;
  category: string;
  brandContext?: string;
  automatic?: boolean;
};

export function applyCodeCategoryRules<T extends Transaction>(transactions: T[], rules: CodeCategoryRuleLike[]): T[] {
  return transactions.map((transaction) => {
    if (transaction.categoryAssignmentSource === "manual" || ((transaction.brandCandidates?.length || 0) > 1 && transaction.businessUnitMappingSource !== "manual")) return transaction;
    const context = [...new Set((transaction.businessUnits ?? [transaction.businessUnit]).filter((value) => value && value !== "unassigned"))].sort().join("+") || "unassigned";
    const rule = rules.find((candidate) => candidate.automatic === true && candidate.brandContext === context && candidate.uniqueCode === transaction.uniqueCode && candidate.direction === transaction.direction);
    return rule ? { ...transaction, category: rule.category, categoryAssignmentSource: "rule" as const }
      : transaction.categoryAssignmentSource === "rule" ? { ...transaction, category: "uncategorized", categoryAssignmentSource: "unassigned" as const } : transaction;
  });
}

function realPaymentReference(transaction: Transaction) {
  const reference = transaction.paymentRef.trim().toUpperCase();
  return reference && !reference.startsWith("AUTO-") ? reference : "";
}

function comparableOtherIdentityValues(transaction: Transaction) {
  return unique([
    transaction.sourceAccount,
    transaction.beneficiaryAccount,
    transaction.beneficiaryName || "",
  ].map(simplify).filter((value) => value.length >= 3));
}

function comparableSenderName(transaction: Transaction) {
  const senderName = simplify(transaction.sourceName || "");
  return senderName.length >= 3 ? senderName : "";
}

function sameTransferMoment(incoming: Transaction, candidate: Transaction) {
  if (incoming.transactionTimestamp && candidate.transactionTimestamp) {
    return incoming.transactionTimestamp.slice(0, 16) === candidate.transactionTimestamp.slice(0, 16);
  }
  if (!incoming.transactionTimestamp && !candidate.transactionTimestamp) {
    return Boolean(incoming.date && candidate.date && incoming.date === candidate.date);
  }
  return false;
}

function duplicateEvidence(incoming: Transaction, candidate: Transaction) {
  const incomingReference = realPaymentReference(incoming);
  const candidateReference = realPaymentReference(candidate);
  const bothReferencesAvailable = Boolean(incomingReference && candidateReference);
  const sameReference = Boolean(incomingReference && candidateReference && incomingReference === candidateReference);
  // Existing rules already reject different real references; skip irrelevant work.
  if (bothReferencesAvailable && !sameReference) return { reasons: [] as DuplicateReason[], score: 0 };
  const sameDateTime = sameTransferMoment(incoming, candidate);
  const incomingDescription = normalizeNarrative(incoming.subject);
  const candidateDescription = normalizeNarrative(candidate.subject);
  const sameDescription = Boolean(incomingDescription && candidateDescription && incomingDescription === candidateDescription);
  const sameAmount = incoming.amount === candidate.amount;
  const sameDirection = incoming.direction === candidate.direction;
  const incomingSender = comparableSenderName(incoming);
  const candidateSender = comparableSenderName(candidate);
  const bothSendersAvailable = Boolean(incomingSender && candidateSender);
  const sameSender = Boolean(incomingSender && candidateSender && incomingSender === candidateSender);
  const candidateIdentities = new Set(comparableOtherIdentityValues(candidate));
  const sameAccount = comparableOtherIdentityValues(incoming).some((value) => candidateIdentities.has(value));

  const reasons: DuplicateReason[] = [];
  if (sameReference) reasons.push("reference");
  if (sameSender) reasons.push("sender");
  if (sameDateTime) reasons.push("dateTime");
  if (sameDescription) reasons.push("description");
  if (sameAmount) reasons.push("amount");
  if (sameDirection) reasons.push("direction");
  if (sameAccount) reasons.push("account");

  const score = (sameReference ? 1000 : 0)
    + (sameSender ? 300 : 0)
    + (sameDateTime ? 45 : 0)
    + (sameDescription ? 25 : 0)
    + (sameAmount ? 20 : 0)
    + (sameAccount ? 10 : 0)
    + (sameDirection ? 5 : 0);
  const hasFallbackIdentity = sameDateTime || sameDescription || sameAccount;
  const eligible = bothReferencesAvailable
    ? sameReference
    : bothSendersAvailable
      ? sameSender && sameDescription && sameDirection && sameDateTime
      : sameAmount && sameDirection && hasFallbackIdentity;
  return { reasons: eligible ? reasons : [], score };
}

export function analyzeDuplicateTransactions(existing: Transaction[], incoming: Transaction[]): DuplicateAnalysis {
  const reviewItems: DuplicateReviewItem[] = [];
  let referenceMatchCount = 0;
  let newTransactionCount = 0;

  incoming.forEach((transaction, incomingIndex) => {
    const candidates = existing
      .map((candidate) => ({ transaction: candidate, ...duplicateEvidence(transaction, candidate) }))
      .filter((candidate) => candidate.reasons.length > 0)
      .sort((first, second) => second.score - first.score || second.reasons.length - first.reasons.length)
      .slice(0, 5)
      .map(({ transaction: candidate, reasons }) => ({ transaction: candidate, reasons }));
    if (candidates.length) {
      if (candidates.some((candidate) => candidate.reasons.includes("reference"))) referenceMatchCount += 1;
      reviewItems.push({ incomingIndex, incoming: transaction, candidates });
    } else newTransactionCount += 1;
  });

  return { referenceMatchCount, newTransactionCount, reviewItems };
}

export function deduplicateTransactions(
  existing: Transaction[],
  incoming: Transaction[],
  mergeTargets: Record<number, string> = {},
  options?: { reviewedIndexes: ReadonlySet<number>; linkSourcesOnly: boolean },
) {
  if (existing.some((item) => !Array.isArray(item.identityKeys))) {
    return { transactions: existing, duplicateCount: 0, insertedCount: 0, enrichedCount: 0, ambiguousCount: existing.length };
  }
  const transactions = [...existing];
  const targetIndexes = new Map<string, number>();
  existing.forEach((row, index) => { if (!targetIndexes.has(row.id)) targetIndexes.set(row.id, index); });
  let ambiguousCount = 0;

  let duplicateCount = 0;
  let insertedCount = 0;
  let enrichedCount = 0;
  for (const [incomingIndex, transaction] of incoming.entries()) {
    let duplicateIndex = -1;
    const selectedTargetId = mergeTargets[incomingIndex];
    if (selectedTargetId) {
      const selectedIndex = targetIndexes.get(selectedTargetId);
      if (selectedIndex !== undefined && !transactions[selectedIndex].deletedAt) duplicateIndex = selectedIndex;
    }
    if (duplicateIndex >= 0) {
      const previous = transactions[duplicateIndex];
      const merged = options?.linkSourcesOnly ? {
        ...previous,
        sourceFiles: unique([...(previous.sourceFiles || [previous.sourceFile]), ...(transaction.sourceFiles || [transaction.sourceFile])]),
        sourceBatchIds: unique([...(previous.sourceBatchIds || []), ...(transaction.sourceBatchIds || [])]),
        sourceFile: unique([...previous.sourceFile.split(" · "), ...transaction.sourceFile.split(" · ")]).join(" · "),
      } : mergeTransaction(previous, transaction);
      if (options?.linkSourcesOnly ? merged.sourceFile !== previous.sourceFile || merged.sourceBatchIds.length !== (previous.sourceBatchIds?.length || 0) : JSON.stringify(merged) !== JSON.stringify(previous)) enrichedCount += 1;
      transactions[duplicateIndex] = merged;
      duplicateCount += 1;
    } else {
      if (options ? options.reviewedIndexes.has(incomingIndex) : existing.some((candidate) => duplicateEvidence(transaction, candidate).reasons.length > 0)) ambiguousCount += 1;
      transactions.push(transaction);
      insertedCount += 1;
    }
  }
  return { transactions, duplicateCount, insertedCount, enrichedCount, ambiguousCount };
}

export function rowsFromGrid(grid: unknown[][]) {
  let headerIndex = 0;
  let bestScore = -1;
  for (let index = 0; index < Math.min(grid.length, 15); index += 1) {
    const cells = (grid[index] || []).map(simplify);
    const score = cells.filter((cell) =>
      Object.values(headerAliases).flat().some((alias) => cell === alias || cell.includes(alias)),
    ).length;
    if (score > bestScore) {
      bestScore = score;
      headerIndex = index;
    }
  }
  const headers = (grid[headerIndex] || []).map((value, index) => String(value || `column_${index + 1}`).trim());
  const metadataRows = grid.slice(0, headerIndex);
  const metadataText = metadataRows.flat().map((value) => String(value || "").trim()).filter(Boolean).join(" | ");
  const explicitAccount = metadataRows.find((cells) => simplify(cells?.[0]) === "account")?.[1];
  const accountNumber = String(explicitAccount || metadataText.match(/(?:account|rekening)[^0-9]*(\d{6,})/i)?.[1] || "").replace(/^'/, "");
  const entityCandidate = String(metadataRows[0]?.[0] || "").split("/")[0].trim();
  const entity = simplify(entityCandidate) === "filter"
    ? ""
    : /^rekening\b/i.test(entityCandidate) ? "" : entityCandidate;
  const accountLabel = [entity, accountNumber].filter(Boolean).join(" · ");
  return grid.slice(headerIndex + 1).map((cells) =>
    ({
      ...Object.fromEntries(headers.map((header, index) => [header, cells[index] ?? ""])),
      __accountLabel: accountLabel,
    }),
  );
}

function splitDelimitedLine(line: string, delimiter: string) {
  const result: string[] = [];
  let current = "";
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"') {
      if (quoted && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else quoted = !quoted;
    } else if (char === delimiter && !quoted) {
      result.push(current.trim());
      current = "";
    } else current += char;
  }
  result.push(current.trim());
  return result;
}

export function parseDelimited(text: string) {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((line) => line.trim());
  const candidates = [",", ";", "\t", "|"];
  const delimiter = candidates
    .map((value) => ({ value, count: (lines[0]?.match(new RegExp(value === "|" ? "\\|" : value, "g")) || []).length }))
    .sort((a, b) => b.count - a.count)[0]?.value || ",";
  return lines.map((line) => splitDelimitedLine(line, delimiter));
}
